/**
 * LaunchPage — Hub central de Polynator
 *
 * Ruta: /launch
 *
 * Hub para acceder a todos los módulos internos y herramientas externas.
 * Accesible desde el login normal (Redux) o desde el SSO Launcher (localStorage).
 */

// libraries
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';

// domain
import { useRoleForTools } from '@domains/user';
import {
  getEmail,
  getRole,
  getIsRoleIsPolygonCreator,
  getIsDocumentReviewerRole,
  setDefaultState,
} from '@domains/user/store/slices/user-slice/user-slice';
import { RootState } from '@store';
import { UserRoles } from '@domains/user/utils/roles';
import { RolesPolynatorEnum } from '@domains/user/store/api-slices/roles-polynator.endpoint';
import { DropdownCountry } from '@domains/user/components/DropdownCountry/DropdownCountry';

// config & utils
import { EXTERNAL_TOOLS, ExternalTool, getCurrentEnvironment, getToolUrl } from '../../config/external-tools';
import { openExternalTool } from '../../utils/external-tool.service';

// local
import {
  InternalModule,
  MODULE_GEORREF,
  MODULE_POLYNATOR,
  MODULE_POLYGON_VIEWER,
  MODULE_DOC_REVIEW,
  MODULE_LA_CENTRAL,
  ENV_LABELS,
  ENV_COLORS,
  ENV_LABELS_SHORT,
  SSO_METHODS,
} from './launch.constants';
import { hasLaunchAuth, clearLaunchAuth } from './launch.utils';
import {
  PageWrapper,
  NavBar,
  NavBrand,
  NavTitle,
  NavSubtitle,
  NavActions,
  EnvBadge,
  LogoutButton,
  SectionWrapper,
  SectionTitle,
  SectionSubtitle,
  SectionDivider,
  CardWrapper,
  CardIcon,
  CardTitle,
  CardDescription,
  CardButton,
  ExternalCardWrapper,
  ToolCardHeader,
  EnvBadgeSmall,
  UrlBox,
  SsoMethodLabel,
  OpeningCard,
  OpeningLabel,
  LocalhostBanner,
  FooterBox,
} from './styles';

// ─────────────────────────────────────────────────────────────────────────────
// Section header
// ─────────────────────────────────────────────────────────────────────────────
const SectionHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <SectionWrapper>
    <SectionTitle>{title}</SectionTitle>
    {subtitle && <SectionSubtitle>{subtitle}</SectionSubtitle>}
    <SectionDivider />
  </SectionWrapper>
);

// ─────────────────────────────────────────────────────────────────────────────
// Card de módulo interno
// ─────────────────────────────────────────────────────────────────────────────
interface InternalModuleCardProps {
  module: InternalModule;
  onOpen: (path: string) => void;
}

const InternalModuleCard = ({ module, onOpen }: InternalModuleCardProps) => (
  <CardWrapper>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <CardIcon $color={module.color}>{module.icon}</CardIcon>
      <CardTitle>{module.name}</CardTitle>
    </div>
    <CardDescription>{module.description}</CardDescription>
    <CardButton onClick={() => onOpen(module.path)}>
      Abrir →
    </CardButton>
  </CardWrapper>
);

// ─────────────────────────────────────────────────────────────────────────────
// Card de herramienta externa (SSO)
// ─────────────────────────────────────────────────────────────────────────────
interface ToolCardProps {
  tool: ExternalTool;
  env: 'local' | 'qa' | 'prod';
  onOpen: (tool: ExternalTool) => void;
}

const ToolCard = ({ tool, env, onOpen }: ToolCardProps) => (
  <ExternalCardWrapper>
    <div>
      <ToolCardHeader>
        <CardTitle>{tool.name}</CardTitle>
        <EnvBadgeSmall $color={ENV_COLORS[env]}>{ENV_LABELS_SHORT[env]}</EnvBadgeSmall>
      </ToolCardHeader>
      <CardDescription>{tool.description}</CardDescription>
    </div>

    <UrlBox>
      <span className="label">URL: </span>
      {getToolUrl(tool)}{tool.ssoPath}
    </UrlBox>

    <SsoMethodLabel>
      <strong>Método SSO:</strong> {SSO_METHODS[env]}
    </SsoMethodLabel>

    <CardButton onClick={() => onOpen(tool)}>Abrir con SSO →</CardButton>
  </ExternalCardWrapper>
);

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
export const LaunchPage = () => {
  const navigate                    = useNavigate();
  const dispatch                    = useDispatch();
  const [ready, setReady]           = useState(false);
  const [openedTool, setOpenedTool] = useState<string | null>(null);
  const env = getCurrentEnvironment();

  // Redux
  const email              = useSelector(getEmail);
  const roles              = useSelector(getRole) ?? [];
  const countriesOptions   = useSelector((s: RootState) => s.domainUserAuth.countriesOptions ?? []);
  const isPolygonCreator   = useSelector(getIsRoleIsPolygonCreator);
  const isDocumentReviewer = useSelector(getIsDocumentReviewerRole);
  const isLeader           = roles.includes(UserRoles.IDM_LEAD);
  const hasPolygonRole     =
    roles.includes(RolesPolynatorEnum.PolygonCreator) ||
    roles.includes(RolesPolynatorEnum.PolygonViewer);

  useRoleForTools();

  useEffect(() => {
    if (!email && !hasLaunchAuth()) {
      navigate('/launch-login', { replace: true });
      return;
    }
    setReady(true);
  }, [navigate, email]);

  const handleOpenInternal = (path: string) => navigate(path);

  const handleOpen = (tool: ExternalTool) => {
    setOpenedTool(tool.id);
    openExternalTool(tool);
    setTimeout(() => setOpenedTool(null), 2000);
  };

  const handleLogout = () => {
    clearLaunchAuth();
    dispatch(setDefaultState());
    navigate('/launch-login', { replace: true });
  };

  if (!ready) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const visibleModules: InternalModule[] = [
    MODULE_GEORREF,
    ...(isPolygonCreator ? [MODULE_POLYNATOR] : []),
    ...(!isPolygonCreator && hasPolygonRole ? [MODULE_POLYGON_VIEWER] : []),
    ...(isDocumentReviewer || isLeader ? [MODULE_DOC_REVIEW] : []),
    ...(isLeader ? [MODULE_LA_CENTRAL] : []),
  ];

  return (
    <PageWrapper>
      {/* ── Navbar ──────────────────────────────────────────────────────────── */}
      <NavBar>
        <NavBrand>
          <NavTitle>Polynator</NavTitle>
          <NavSubtitle>
            Inteligencia de Mercados
            {email && <><span className="dot">·</span><span className="email">{email}</span></>}
          </NavSubtitle>
        </NavBrand>

        <NavActions>
          <EnvBadge $color={ENV_COLORS[env]}>{ENV_LABELS[env]}</EnvBadge>
          {countriesOptions.length > 0 && <DropdownCountry isHoveredSidebar={true} />}
          <LogoutButton onClick={handleLogout}>Cerrar sesión</LogoutButton>
        </NavActions>
      </NavBar>

      {/* ── Contenido ───────────────────────────────────────────────────────── */}
      <Container style={{ paddingTop: '7rem', paddingBottom: '3rem' }}>

        {/* Módulos internos */}
        {!!email && (
          <div style={{ marginBottom: '3rem' }}>
            <SectionHeader title="Módulos" subtitle="Herramientas internas de Polynator" />
            <Row className="g-4">
              {visibleModules.map((mod) => (
                <Col key={mod.id} xs={12} sm={6} lg={3}>
                  <InternalModuleCard module={mod} onOpen={handleOpenInternal} />
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Herramientas externas */}
        <div>
          <SectionHeader title="Herramientas externas" subtitle="Acceso con SSO automático" />

          {env === 'local' && (
            <LocalhostBanner>
              <strong>⚠️ Modo Localhost:</strong>{' '}
              Asegúrate de que las herramientas destino estén corriendo en sus puertos locales.
            </LocalhostBanner>
          )}

          <Row className="g-4">
            {EXTERNAL_TOOLS.map((tool) => (
              <Col key={tool.id} xs={12} md={6} lg={4}>
                {openedTool === tool.id ? (
                  <OpeningCard>
                    <Spinner animation="border" style={{ color: '#7c3aed' }} />
                    <OpeningLabel>Abriendo {tool.name}...</OpeningLabel>
                  </OpeningCard>
                ) : (
                  <ToolCard tool={tool} env={env} onOpen={handleOpen} />
                )}
              </Col>
            ))}
          </Row>
        </div>

        {/* Footer — solo en local */}
        {env === 'local' && (
          <FooterBox>
            <strong>📋 Cómo funciona:</strong>
            <ol>
              <li>Los módulos internos navegan directamente a la ruta correspondiente</li>
              <li>Las herramientas externas abren una nueva pestaña con SSO automático</li>
              <li>Los módulos visibles dependen del rol asignado en cada herramienta</li>
            </ol>
          </FooterBox>
        )}
      </Container>
    </PageWrapper>
  );
};
